/**
 * Beendete Anfragen — abgebrochene Anfragen mit Stage (Phase vor dem
 * Abbruch) und Begründung. Eine gemeinsame Komponente für beide Scopes:
 *   scope='pb' → Seite „Beendete Anfragen (PB)"  (menu.beendete_anfragen)
 *   scope='bk' → Seite „Beendete Anfragen (BK)"  (menu.beendete_anfragen_bk)
 *
 * Daten: GET /v2/admin/anfrage-abbrueche?scope=… (neuester Abbruch je User,
 * nur aktuell abgebrochene, users.phase < 0). Zeilenklick öffnet das Profil
 * (PB → Familienkonto-Detail, BK → BK-Detail).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Input } from '../../components/ui';
import { getThemeColors, DENSITY_VALUES } from '../../lib/theme';
import { navigateGlobal, buildPath } from '../../lib/route';
import { rowLinkProps } from '../../lib/openLink';
import { formatDateTime } from '../../lib/dateFormat';
import { v2BeendeteAnfragen, type BeendeteAnfrage, type BeendeteAnfragenMeta } from '../../lib/v1';
import type { Density } from '../../types';

const PER_PAGE = 50;

interface Props {
  scope: 'pb' | 'bk';
  dark: boolean;
  density: Density;
}

export function BeendeteAnfragenPage({ scope, dark, density }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const d = DENSITY_VALUES[density];

  const [rows, setRows] = useState<BeendeteAnfrage[]>([]);
  const [meta, setMeta] = useState<BeendeteAnfragenMeta | null>(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  // Suche entprellen (300 ms), damit nicht jeder Tastendruck lädt.
  useEffect(() => {
    const h = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(h);
  }, [q]);

  const load = useCallback(async (targetPage: number, append: boolean) => {
    append ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const res = await v2BeendeteAnfragen.list(scope, {
        page: targetPage, per_page: PER_PAGE, q: qDebounced || undefined,
      });
      setRows(prev => (append ? [...prev, ...res.rows] : res.rows));
      setMeta(res.meta);
      setPage(res.meta.page);
      setForbidden(false);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setForbidden(true);
      } else {
        setError(e instanceof Error ? e.message : tr('anfragen_beendet.error.load'));
      }
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [scope, qDebounced, tr]);

  useEffect(() => { void load(1, false); }, [load]);

  const profilePath = (row: BeendeteAnfrage): string | null => {
    if (scope === 'bk') return buildPath('betreuung', String(row.user_id));
    return row.family_account_id != null ? buildPath('pflege', String(row.user_id)) : null;
  };

  const openProfile = (row: BeendeteAnfrage) => {
    if (scope === 'bk') {
      navigateGlobal('betreuung', String(row.user_id));
    } else if (row.family_account_id != null) {
      navigateGlobal('pflege', String(row.user_id));
    }
  };

  const th: React.CSSProperties = {
    textAlign: 'left', padding: `${d.rowY}px 14px`, fontSize: 11.5, fontWeight: 700,
    color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = {
    padding: `${d.rowY}px 14px`, fontSize: 13, color: t.text,
    borderBottom: `1px solid ${t.borderSoft}`, verticalAlign: 'top',
  };

  if (forbidden) {
    return (
      <Card dark={dark} style={{ padding: 24 }}>
        <div style={{ fontSize: 13, color: t.muted }}>{tr('anfragen_beendet.forbidden')}</div>
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Suche */}
      <Card dark={dark} style={{ padding: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', maxWidth: 380 }}>
          <Input
            dark={dark}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={tr('anfragen_beendet.search_ph')}
            aria-label={tr('anfragen_beendet.search_ph')}
          />
        </div>
        {meta && (
          <span style={{ fontSize: 12.5, color: t.muted }}>
            {tr('anfragen_beendet.total', { count: meta.total })}
          </span>
        )}
      </Card>

      {error && (
        <Card dark={dark} style={{ padding: 16 }}>
          <div role="alert" style={{ fontSize: 13, color: '#b42318' }}>{error}</div>
        </Card>
      )}

      <Card dark={dark} style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr>
              <th style={th}>{tr('anfragen_beendet.th.name')}</th>
              <th style={th}>{tr('anfragen_beendet.th.stage')}</th>
              <th style={th}>{tr('anfragen_beendet.th.grund')}</th>
              <th style={th}>{tr('anfragen_beendet.th.beschreibung')}</th>
              <th style={th}>{tr('anfragen_beendet.th.cancelled_at')}</th>
              <th style={th}>{tr('anfragen_beendet.th.cancelled_by')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={td} colSpan={6}>{tr('common.loading_short')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td style={{ ...td, color: t.muted }} colSpan={6}>{tr('anfragen_beendet.empty')}</td></tr>
            ) : rows.map(row => (
              <tr
                key={row.id}
                {...rowLinkProps(profilePath(row), () => openProfile(row))}
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = t.hover; }}
                onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
              >
                <td style={td}>
                  <div style={{ fontWeight: 600, color: t.heading }}>{row.name || `#${row.user_id}`}</div>
                  {row.email && <div style={{ fontSize: 11.5, color: t.muted }}>{row.email}</div>}
                </td>
                <td style={td}>
                  {row.stage.title ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '3px 10px',
                      background: t.chipBg, color: t.text, whiteSpace: 'nowrap',
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: row.stage.color ?? t.muted, flexShrink: 0 }} />
                      {row.stage.title}
                    </span>
                  ) : (
                    <span style={{ color: t.muted }}>—</span>
                  )}
                </td>
                <td style={td}>{row.grund ?? <span style={{ color: t.muted }}>—</span>}</td>
                <td style={{ ...td, maxWidth: 320 }}>
                  <span style={{ color: row.beschreibung ? t.text : t.muted, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {row.beschreibung ?? '—'}
                  </span>
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(row.created_at)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{row.admin ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && page < meta.last_page && !loading && (
          <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              disabled={loadingMore}
              onClick={() => void load(page + 1, true)}
              style={{
                fontSize: 13, fontWeight: 600, padding: '8px 16px', borderRadius: 8,
                background: 'transparent', border: `1px solid ${t.border}`, color: t.text,
                cursor: loadingMore ? 'wait' : 'pointer', fontFamily: 'Outfit,sans-serif',
              }}
            >
              {loadingMore ? tr('common.loading_short') : tr('anfragen_beendet.load_more')}
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}
